"use client";

import {
	CardCarousel,
	Container,
	type DefaultImageProps,
	type DefaultLinkProps,
	FeaturedText,
	type FeaturedTextProps,
	Heading,
	type HeadingProps,
	Image,
	type ImageProps,
	Link,
	type LinkProps,
	RichText
} from "@pamperedchef/design-system";
import { useId } from "react";

import * as styles from "./SolutionBasedCard.module.scss";

export interface SolutionBasedCardProps<
	ImageComponentProps = DefaultImageProps,
	LinkComponentProps = DefaultLinkProps
> {
	title: HeadingProps;
	description: string;
	image: ImageProps<ImageComponentProps>;
	cta: LinkProps<LinkComponentProps>;
}

/**
 * Solution Based Card
 */
export function SolutionBasedCard<
	ImageComponentProps = DefaultImageProps,
	LinkComponentProps = DefaultLinkProps
>(
	props: SolutionBasedCardProps<ImageComponentProps, LinkComponentProps>
): JSX.Element {
	const id = useId();

	return (
		<article
			aria-labelledby={`solution-based-card-${id}`}
			className={styles.card}
		>
			<Heading
				tag={props.title.tag ?? "h3"}
				styledAs={props.title.styledAs ?? "mdr"}
				id={`solution-based-card-${id}`}
				className={styles.title}
			>
				{props.title.children}
			</Heading>
			<RichText
				className={styles.description}
				content={props.description}
			/>
			<Link<LinkComponentProps>
				{...props.cta}
				kind="primary"
				className={styles["image-wrapper"]}
			>
				<Image<ImageComponentProps>
					{...props.image}
					aspectRatio="1:1"
					objectFit="cover"
					className={styles.image}
					alt={props.image.alt || ""}
				/>
			</Link>
			<Link<LinkComponentProps>
				{...props.cta}
				kind="primary"
				className={styles.cta}
			/>
		</article>
	);
}

export interface SolutionBasedCardsProps<
	ImageComponentProps = DefaultImageProps,
	LinkComponentProps = DefaultLinkProps
> {
	slides?: SolutionBasedCardProps<ImageComponentProps, LinkComponentProps>[];
	featuredText: FeaturedTextProps;
}

/**
 * Wrapper Component
 */
export function SolutionBaseCards<
	ImageComponentProps = DefaultImageProps,
	LinkComponentProps = DefaultLinkProps
>(
	props: SolutionBasedCardsProps<ImageComponentProps, LinkComponentProps>
): JSX.Element {
	return (
		<Container>
			<FeaturedText
				{...props.featuredText}
				className={styles["featured-text"]}
			/>
			{props.slides?.length ? (
				<CardCarousel
					allowOverflow
					slides={props.slides.map((slide, i) => (
						<SolutionBasedCard key={i} {...slide} />
					))}
					className={styles.carousel}
				/>
			) : null}
		</Container>
	);
}
